import { AppParams } from 'AppRoutes';
import {
  Message,
  MsgsCountDocument,
  MsgsCountSubscription,
  MsgsCountSubscriptionVariables,
  MsgsDocument,
  MsgsSubscription,
  MsgsSubscriptionVariables,
} from 'generated/graphql';
import { ExecutionResult } from 'graphql';
import { useSubClient } from 'graphql/client';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Observable } from 'subscriptions-transport-ws';

type MsgSub = Observable<ExecutionResult<MsgsSubscription>>;
type MsgCountSub = Observable<ExecutionResult<MsgsCountSubscription>>;
type MsgState = { data: Message[]; isLoading: boolean };

const DEFAULT_PARAMS = { rows: '10', page: '1' };

export const getQueryString = () =>
  `?page=${DEFAULT_PARAMS.page}&rows=${DEFAULT_PARAMS.rows}`;

export const useMsgs = () => {
  const subClient = useSubClient();
  const [params, setParams] = useSearchParams(DEFAULT_PARAMS);
  const [count, setCount] = useState(0);

  const boxId = parseInt(useParams()[AppParams.LogId] ?? '-1');

  const [subState, setSubState] = useState<MsgState>({
    data: [],
    isLoading: true,
  });

  const rowsParam = parseInt(params.get('rows') ?? DEFAULT_PARAMS.rows);
  const pageParam = parseInt(params.get('page') ?? DEFAULT_PARAMS.page);

  useEffect(() => {
    const offset = (pageParam - 1) * rowsParam;
    const variables: MsgsSubscriptionVariables = {
      boxId,
      offset,
      limit: rowsParam,
    };
    const msgs: MsgSub = subClient.request({ query: MsgsDocument, variables });

    const { unsubscribe } = msgs.subscribe({
      next: ({ data }) => {
        if (data)
          setSubState((p) => ({ ...p, data: data.message, isLoading: false }));
      },
    });

    return () => {
      unsubscribe();
    };
  }, [subClient, boxId, rowsParam, pageParam]);

  useEffect(() => {
    const variables: MsgsCountSubscriptionVariables = { boxId };
    const msgsCount: MsgCountSub = subClient.request({
      query: MsgsCountDocument,
      variables,
    });

    const { unsubscribe } = msgsCount.subscribe({
      next: ({ data }) => {
        if (data) {
          const count = data.message_aggregate.aggregate?.count;
          setCount(count ?? 0);
        }
      },
    });

    return () => unsubscribe();
  }, [subClient, boxId]);

  return {
    isLoading: subState.isLoading,
    data: subState.data,
    count,
    pageCount: Math.ceil(count / rowsParam),
    setParams,
    rowsParam,
    pageParam,
  };
};
